import { useState, useEffect } from 'react';
import { getSIPReminders, createSIPReminder, updateSIPReminder, deleteSIPReminder, getClients } from '../lib/api';
import { formatApiError, getClientDisplayName } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { SearchableSelect } from '../components/ui/searchable-select';
import { Switch } from '../components/ui/switch';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Plus, DotsThree, Trash, Pencil, Bell, WhatsappLogo, Envelope } from '@phosphor-icons/react';

const daySuffix = (d) => d === 1 || d === 21 || d === 31 ? 'st' : d === 2 || d === 22 ? 'nd' : d === 3 || d === 23 ? 'rd' : 'th';

export default function SIPRemindersPage() {
  const [reminders, setReminders] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingReminder, setEditingReminder] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const [formData, setFormData] = useState({
    client_id: '',
    amount: '',
    deduction_day: ''
  });

  const fetchData = async () => {
    try {
      const [remindersRes, clientsRes] = await Promise.all([
        getSIPReminders(),
        getClients()
      ]);
      setReminders(remindersRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const resetForm = () => {
    setFormData({ client_id: '', amount: '', deduction_day: '' });
    setEditingReminder(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        client_id: formData.client_id,
        amount: parseFloat(formData.amount),
        deduction_day: parseInt(formData.deduction_day),
        is_active: true
      };
      if (editingReminder) {
        await updateSIPReminder(editingReminder.id, payload);
        toast.success('Reminder updated');
      } else {
        await createSIPReminder(payload);
        toast.success('SIP Reminder created');
      }
      setShowModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleToggle = async (reminder, checked) => {
    try {
      await updateSIPReminder(reminder.id, { is_active: checked });
      setReminders(reminders.map(r => r.id === reminder.id ? { ...r, is_active: checked } : r));
      toast.success(checked ? 'Reminder activated' : 'Reminder paused');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteSIPReminder(deleteId);
      setReminders(reminders.filter(r => r.id !== deleteId));
      toast.success('Reminder deleted');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setDeleteId(null);
    }
  };

  const openEdit = (reminder) => {
    setFormData({
      client_id: reminder.client_id,
      amount: String(reminder.amount),
      deduction_day: String(reminder.deduction_day)
    });
    setEditingReminder(reminder);
    setShowModal(true);
  };

  const generateWhatsAppMsg = (reminder) => {
    const name = reminder.client?.full_name?.split(' ')[0] || 'Client';
    const amount = reminder.amount?.toLocaleString('en-IN');
    const day = reminder.deduction_day;
    return encodeURIComponent(
      `Hi ${name},\n\nJust a quick reminder that your SIP of ₹${amount} is scheduled for the ${day}${daySuffix(day)}.\n\nPlease ensure sufficient balance in your account to avoid any missed investment.\n\nLet me know if you need any assistance.\n\nWarm regards,\nTeam Finadore`
    );
  };

  const activeCount = reminders.filter(r => r.is_active).length;

  return (
    <div className="p-6 space-y-6" data-testid="sip-reminders-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>SIP Reminders</h1>
          <p className="text-sm text-zinc-500 mt-1">{activeCount} active reminder{activeCount !== 1 ? 's' : ''} · Auto-sends 2 days & 1 day before deduction</p>
        </div>
        <Button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="bg-[#002FA7] hover:bg-[#00227A] text-white rounded-sm"
          data-testid="create-reminder-button"
        >
          <Plus size={18} className="mr-2" /> New Reminder
        </Button>
      </div>

      {/* Table */}
      <div className="border border-zinc-200 rounded-sm overflow-hidden bg-white">
        {loading ? (
          <div className="p-6 space-y-4">{[1, 2, 3].map(i => <Skeleton key={i} className="h-12" />)}</div>
        ) : reminders.length === 0 ? (
          <div className="p-12 text-center">
            <Bell size={48} className="mx-auto text-zinc-300 mb-4" />
            <p className="text-zinc-500">No SIP reminders yet</p>
            <p className="text-xs text-zinc-400 mt-1">Add reminders to auto-notify clients before SIP deduction</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-zinc-50">
                <TableHead className="text-xs uppercase tracking-[0.1em] font-semibold">Client</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.1em] font-semibold">Amount</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.1em] font-semibold">Deduction Day</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.1em] font-semibold">Status</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.1em] font-semibold">Quick Send</TableHead>
                <TableHead className="w-[80px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {reminders.map((reminder) => {
                const whatsappNum = reminder.client?.secondary_phone || reminder.client?.phone;
                return (
                  <TableRow key={reminder.id} data-testid={`reminder-row-${reminder.id}`}>
                    <TableCell>
                      <p className="font-medium text-sm">{reminder.client?.full_name || 'Unknown'}</p>
                      <p className="text-xs text-zinc-400">{reminder.client?.phone}</p>
                    </TableCell>
                    <TableCell className="font-medium">₹{reminder.amount?.toLocaleString('en-IN')}</TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="text-xs">
                        {reminder.deduction_day}{daySuffix(reminder.deduction_day)} of every month
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={reminder.is_active}
                          onCheckedChange={(checked) => handleToggle(reminder, checked)}
                          data-testid={`toggle-${reminder.id}`}
                        />
                        <span className="text-xs text-zinc-500">{reminder.is_active ? 'Active' : 'Paused'}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {whatsappNum && (
                          <a
                            href={`https://wa.me/91${whatsappNum}?text=${generateWhatsAppMsg(reminder)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded bg-green-50 text-green-700 hover:bg-green-100 transition-colors"
                            data-testid={`whatsapp-${reminder.id}`}
                          >
                            <WhatsappLogo size={14} weight="fill" /> WhatsApp
                          </a>
                        )}
                        {reminder.client?.email && (
                          <a
                            href={`mailto:${reminder.client.email}?subject=SIP Reminder - Team Finadore&body=${generateWhatsAppMsg(reminder).replace(/%0A/g, '%0D%0A')}`}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                            data-testid={`email-${reminder.id}`}
                          >
                            <Envelope size={14} /> Email
                          </a>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0"><DotsThree size={16} /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEdit(reminder)}>
                            <Pencil size={16} className="mr-2" /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setDeleteId(reminder.id)} className="text-red-600">
                            <Trash size={16} className="mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={() => { setShowModal(false); resetForm(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingReminder ? 'Edit' : 'New'} SIP Reminder</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label className="text-xs uppercase font-semibold">Client *</Label>
              <SearchableSelect
                value={formData.client_id}
                onValueChange={(v) => setFormData(p => ({ ...p, client_id: v }))}
                options={clients.map(c => ({ value: c.id, label: `${getClientDisplayName(c)} ${c.phone ? `(${c.phone})` : ''}` }))}
                placeholder="Select client"
                searchPlaceholder="Search clients..."
                data-testid="reminder-client-select"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase font-semibold">SIP Amount (₹) *</Label>
                <Input
                  type="number"
                  value={formData.amount}
                  onChange={(e) => setFormData(p => ({ ...p, amount: e.target.value }))}
                  required
                  min="1"
                  className="rounded-sm"
                  data-testid="reminder-amount-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase font-semibold">Deduction Day *</Label>
                <SearchableSelect
                  value={formData.deduction_day}
                  onValueChange={(v) => setFormData(p => ({ ...p, deduction_day: v }))}
                  options={Array.from({ length: 28 }, (_, i) => ({ value: String(i + 1), label: `${i + 1}${daySuffix(i + 1)}` }))}
                  placeholder="Day"
                  data-testid="reminder-day-select"
                />
              </div>
            </div>

            {formData.client_id && formData.amount && formData.deduction_day && (
              <div className="bg-zinc-50 p-3 rounded-sm text-sm border border-zinc-100">
                <p className="text-xs uppercase font-semibold text-zinc-500 mb-2">Message Preview</p>
                <p className="whitespace-pre-line text-zinc-700 text-xs leading-relaxed">
                  {`Hi ${(clients.find(c => c.id === formData.client_id)?.full_name || '').split(' ')[0]},\n\nJust a quick reminder that your SIP of ₹${parseFloat(formData.amount || 0).toLocaleString('en-IN')} is scheduled for the ${formData.deduction_day}${daySuffix(parseInt(formData.deduction_day))}.\n\nPlease ensure sufficient balance in your account to avoid any missed investment.\n\nLet me know if you need any assistance.\n\nWarm regards,\nTeam Finadore`}
                </p>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => { setShowModal(false); resetForm(); }}>Cancel</Button>
              <Button type="submit" className="bg-[#002FA7] text-white" disabled={!formData.client_id || !formData.amount || !formData.deduction_day} data-testid="submit-reminder-button">
                {editingReminder ? 'Update' : 'Create'} Reminder
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Reminder</AlertDialogTitle>
            <AlertDialogDescription>Are you sure? This will stop all future reminders for this SIP.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
